import { useState } from "react";
import { DataTable } from "@/components/data-tables/data-table";
import { createUserManagementColumns, UserManagementSearchableColumns } from "./UserManagementDataTableColumns";
import { useDataTable } from "@/hooks/useDataTable";
import { Button } from "@/components/ui/button";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import UserManagementDialog from "@/components/dialogs/UserManagementDialog";
import InviteUserDialog from "@/components/dialogs/InviteUserDialog";
import { z } from "zod";
import { findUser } from "@shared/validators/user-validator";
import { useDeleteUserMutation } from "@/api/mutations/UserMutations";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";

type User = z.infer<typeof findUser>;

interface UserManagementActions {
    onViewDetails?: (user: User) => void;
    onEdit?: (user: User) => void;
    onToggleEnabled?: (user: User) => void;
    onDuplicate?: (user: User) => void;
    onDelete?: (user: User) => void;
    onResendInvite?: (user: User) => void;
    onCopyInviteLink?: (user: User) => void;
}

type UserManagementDataTableProps = {
    data: User[];
    actions?: UserManagementActions;
};

const UserManagementDataTable = ({ data, actions }: UserManagementDataTableProps) => {
    const { t } = useTranslation();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    // Initialize mutations
    const deleteUserMutation = useDeleteUserMutation([]);
    const resendInviteMutation = trpc.invitations.resend.useMutation();
    const utils = trpc.useUtils();

    // Default action callbacks
    const defaultActions: UserManagementActions = {
        onViewDetails: (user) => {
            toast.info(t("dataTables.viewingDetailsFor", { name: user.name }));
            // TODO: Implement view details modal
        },
        onEdit: (user) => {
            setSelectedUser(user);
            setEditDialogOpen(true);
        },
        onToggleEnabled: (user) => {
            toast.info(t("dataTables.toggleEnabledFor", { name: user.name }));
            // TODO: Implement toggle enabled
        },
        onDuplicate: (user) => {
            toast.info(t("dataTables.duplicateUser", { name: user.name }));
            // TODO: Implement duplicate user
        },
        onDelete: (user) => {
            console.log("Delete user clicked:", user.name);
            if (confirm(t("dataTables.confirmDeleteUser", { name: user.name }))) {
                console.log("Confirmed, deleting user:", user.id);
                deleteUserMutation.mutate({ ids: [user.id] });
            }
        },
        onResendInvite: async (user) => {
            try {
                // Find the invitation for this user
                const invitations = await utils.invitations.findMany.fetch();
                const invitation = invitations.find((inv) => inv.email === user.email && inv.status === "pending");

                if (!invitation) {
                    toast.error(t("invitations.messages.noInvitationFound"));
                    return;
                }

                await resendInviteMutation.mutateAsync({ id: invitation.id });
                toast.success(t("invitations.messages.invitationResent"));
            } catch (error: any) {
                toast.error(error.message || t("invitations.messages.invitationFailed"));
            }
        },
        onCopyInviteLink: async (user) => {
            try {
                const result = await utils.invitations.getInvitationLink.fetch({ email: user.email });
                await navigator.clipboard.writeText(result.invitationLink);
                toast.success(t("invitations.messages.linkCopied"));
            } catch (error: any) {
                toast.error(error.message || t("invitations.messages.copyFailed"));
            }
        },
    };

    const finalActions = { ...defaultActions, ...actions };
    const columns = createUserManagementColumns(finalActions);

    const { table } = useDataTable({
        columns,
        data,
        pageCount: 1,
        searchableColumns: UserManagementSearchableColumns,
    });

    const rows = table.getSelectedRowModel();
    const ids = rows.flatRows.map((row) => row.original.id) as string[];

    const handleDeleteUsers = () => {
        if (confirm(t("dataTables.confirmDeleteUsers"))) {
            deleteUserMutation.mutate({ ids });
        }
    };

    const handleCreateUser = () => {
        setSelectedUser(null); // Clear any selected user for fresh creation
        setCreateDialogOpen(true);
    };

    const handleInviteUser = () => {
        setInviteDialogOpen(true);
    };

    return (
        <>
            <DataTable
                table={table}
                columns={columns}
                searchableColumns={UserManagementSearchableColumns}
                newRowButton={
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="h-8" onClick={handleInviteUser}>
                            <Mail className="mr-2 size-4" aria-hidden="true" />
                            {t("dataTables.inviteUser")}
                        </Button>
                        <Button variant="outline" size="sm" className="h-8" onClick={handleCreateUser}>
                            <PlusCircledIcon className="mr-2 size-4" aria-hidden="true" />
                            {t("dataTables.newUser")}
                        </Button>
                    </div>
                }
                deleteRowsAction={handleDeleteUsers}
            />

            {/* Create User Dialog */}
            <UserManagementDialog
                open={createDialogOpen}
                onOpenChange={setCreateDialogOpen}
                user={selectedUser}
                mode="create"
            />

            {/* Edit User Dialog */}
            <UserManagementDialog
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                user={selectedUser}
                mode="edit"
            />

            {/* Invite User Dialog */}
            <InviteUserDialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} />
        </>
    );
};

export default UserManagementDataTable;
